import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Share2, Bookmark } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../App";

const FeedCard = ({ project }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [likes, setLikes] = useState(project.likes || 0);
  const [liked, setLiked] = useState(false);

  const handleLike = async (e) => {
    e.stopPropagation();
    if (liked) return;
    
    try {
      await axios.post(`${API}/projects/${project.id}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Failed to like:", error);
    }
    setLikes(prev => prev + 1);
    setLiked(true);
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: project.title,
          text: project.description,
          url: window.location.origin + `/project/${project.id}`
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    }
  };

  return (
    <article 
      className="bg-black border-b border-white/10 pb-6"
      data-testid={`feed-card-${project.id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 p-0.5">
          <div className="w-full h-full rounded-full bg-zinc-900 flex items-center justify-center">
            <span className="text-sm font-bold text-emerald-500">CL</span>
          </div>
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-white">colacicco_legno</p>
          <p className="text-xs text-white/50">{project.category}</p>
        </div>
      </div>

      {/* Image */}
      <button 
        onClick={() => navigate(`/project/${project.id}`)}
        className="w-full aspect-[4/5] bg-zinc-900 overflow-hidden group"
      >
        <img
          src={project.images?.[0] || "https://images.unsplash.com/photo-1760072513403-d70003481414?w=800"}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </button>

      {/* Actions */}
      <div className="px-4 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <button 
              onClick={handleLike}
              className={`transition-all duration-200 ${liked ? "text-red-500 scale-110" : "text-white hover:text-white/70"}`}
              data-testid={`like-btn-${project.id}`}
            >
              <Heart size={26} strokeWidth={1.5} fill={liked ? "currentColor" : "none"} />
            </button>
            <button 
              onClick={() => navigate(`/project/${project.id}`)}
              className="text-white hover:text-white/70 transition-colors"
            >
              <MessageCircle size={26} strokeWidth={1.5} />
            </button>
            <button 
              onClick={handleShare}
              className="text-white hover:text-white/70 transition-colors"
            >
              <Share2 size={26} strokeWidth={1.5} />
            </button>
          </div>
          <button className="text-white hover:text-white/70 transition-colors">
            <Bookmark size={26} strokeWidth={1.5} />
          </button>
        </div>

        {/* Likes */}
        <p className="text-sm font-medium text-white mb-2">{likes} Mi piace</p>

        {/* Caption */}
        <p className="text-sm text-white">
          <span className="font-medium">colacicco_legno</span>{" "}
          <span className="text-white/80">{project.title}</span>
        </p>
        <p className="text-sm text-white/60 mt-1 line-clamp-2">{project.description}</p>

        {/* Date */}
        <p className="text-xs text-white/40 mt-2 uppercase tracking-wider">
          {new Date(project.created_at).toLocaleDateString("it-IT", {
            day: "numeric",
            month: "long",
            year: "numeric"
          })}
        </p>
      </div>
    </article>
  );
};

export default FeedCard;
